/**
 * SmartAssistant — floating AI shopping assistant (Claude Opus 4.8).
 * Bottom-left FAB → chat panel. Multi-turn, grounded in the live catalogue;
 * renders Add-to-Cart cards for products the assistant recommends.
 * Conversation persists in sessionStorage across page navigation.
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, ShoppingCart, Loader2 } from 'lucide-react';
import Kinyonga from './Kinyonga';
import { useCart } from '../hooks/useCart';
import { useTrack } from '../hooks/useTrack';
import { Product } from '../types';

interface ChatMsg { role: 'user' | 'assistant'; content: string; products?: AiProduct[] }
interface AiProduct {
  id: string; name: string; category: string; price: number;
  unit: string; image: string | null; stock: number;
}

const STORE_KEY = 'mbegu_ai_chat';
const SUGGESTIONS = [
  'Which tomato variety suits my region?',
  'How much for 1 acre of cabbages?',
  'How does delivery work?',
  'I need seedlings for a school project',
];

const WELCOME: ChatMsg = {
  role: 'assistant',
  content:
    'Habari! I\'m the Mbegu assistant 🌱 — ask me anything about seedlings, prices, planting, or delivery, and I\'ll help you order the right varieties for your farm.',
};

const SmartAssistant: React.FC = () => {
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [msgs, setMsgs]       = useState<ChatMsg[]>(() => {
    try {
      const saved = sessionStorage.getItem(STORE_KEY);
      if (saved) { const p = JSON.parse(saved); if (Array.isArray(p) && p.length) return p; }
    } catch { /* fresh chat */ }
    return [WELCOME];
  });
  const { addItem } = useCart();
  const { track } = useTrack();
  const endRef = useRef<HTMLDivElement>(null);
  const openedOnce = useRef(false);

  useEffect(() => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(msgs.slice(-24))); } catch { /* quota */ }
  }, [msgs]);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, open, loading]);

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || loading) return;
    setInput('');
    const next: ChatMsg[] = [...msgs, { role: 'user', content: q }];
    setMsgs(next);
    setLoading(true);
    track({ event: 'ai_chat_message', page: window.location.pathname, extra: { q: q.slice(0, 120) } });

    try {
      const base = process.env.REACT_APP_API_URL ?? '/.netlify/functions';
      const res = await fetch(`${base}/aiAssistant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: next.filter(m => m !== WELCOME).map(m => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json();
      setMsgs(m => [...m, {
        role: 'assistant',
        content: data.response ?? 'Sorry, something went wrong — please try again.',
        products: Array.isArray(data.products) ? data.products : [],
      }]);
    } catch {
      setMsgs(m => [...m, {
        role: 'assistant',
        content: 'I couldn\'t reach the server. Please check your connection, or WhatsApp us on +254721977639.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (p: AiProduct) => {
    const product: Product = {
      id: p.id, name: p.name, description: '', price: p.price,
      image: p.image ?? '', category: p.category, stock: p.stock,
    };
    addItem(product, 1);
    track({ event: 'product_added_to_cart', productName: p.name, productCategory: p.category, extra: { via: 'ai_assistant' } });
    setMsgs(m => [...m, { role: 'assistant', content: `Added ${p.name} to your cart ✅ — open the cart when you're ready to pay with M-Pesa.` }]);
  };

  const toggleOpen = () => {
    setOpen(v => !v);
    if (!openedOnce.current) {
      openedOnce.current = true;
      track({ event: 'ai_chat_opened', page: window.location.pathname });
    }
  };

  return (
    <>
      {/* FAB — bottom-left (FloatingCTA owns bottom-right) */}
      <button
        onClick={toggleOpen}
        aria-label={open ? 'Close Mbegu assistant' : 'Ask the Mbegu assistant'}
        className="fixed bottom-6 left-4 md:left-6 z-50 flex items-center gap-2 rounded-full shadow-lg
                   px-4 h-12 font-semibold text-sm text-white transition-transform hover:scale-105"
        style={{ background: 'var(--forest, #13492F)' }}>
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        <span className="hidden sm:inline">{open ? 'Close' : 'Ask Mbegu'}</span>
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-50 bottom-20 left-2 right-2 sm:left-6 sm:right-auto sm:w-[380px]
                     rounded-2xl border border-border bg-card text-card-foreground shadow-2xl
                     flex flex-col overflow-hidden"
          style={{ maxHeight: 'min(70vh, 560px)' }}>

          {/* Header */}
          <div className="px-4 py-3 flex items-center gap-2.5 text-white" style={{ background: 'var(--forest, #13492F)' }}>
            <Kinyonga variant="head" size={32} className="flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold leading-tight">Kinyonga · Mbegu Assistant</p>
              <p className="text-[10px] opacity-75">Seedling advice · prices · ordering</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat"
              className="w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-[220px]">
            {msgs.map((m, i) => (
              <div key={i}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'ml-auto bg-primary text-white rounded-br-sm'
                    : 'mr-auto bg-muted text-foreground rounded-bl-sm'
                }`}>
                  {m.content}
                </div>

                {/* Product cards */}
                {m.products && m.products.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {m.products.map(p => (
                      <div key={p.id} className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-2.5 py-2">
                        {p.image
                          ? <img src={p.image} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                          : <span className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">🌱</span>}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{p.name}</p>
                          <p className="text-[11px] text-muted-foreground font-mono">KES {p.price} · {p.unit}</p>
                        </div>
                        <button onClick={() => addToCart(p)}
                          className="flex items-center gap-1 rounded-full text-white text-[11px] font-semibold px-2.5 py-1.5 hover:opacity-90"
                          style={{ background: 'var(--leaf, #2F9E5E)' }}>
                          <ShoppingCart className="w-3 h-3" /> Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="mr-auto bg-muted rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
              </div>
            )}

            {/* Suggestion chips on a fresh chat */}
            {msgs.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => send(s)}
                    className="text-[11px] px-2.5 py-1.5 rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={e => { e.preventDefault(); send(input); }}
            className="border-t border-border p-2 flex items-center gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask about seedlings, prices, delivery…"
              aria-label="Message the Mbegu assistant"
              className="flex-1 bg-transparent text-sm px-2 py-2 outline-none placeholder:text-muted-foreground"
              maxLength={1000}
            />
            <button type="submit" disabled={loading || !input.trim()} aria-label="Send"
              className="w-9 h-9 rounded-full flex items-center justify-center text-white disabled:opacity-40"
              style={{ background: 'var(--forest, #13492F)' }}>
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default SmartAssistant;
